import { useMemo } from 'react';

import type { UUID } from '@aline/common';
import { Queries } from '@aline/common';
import { useDocumentParams } from 'src/helpers';
import { useGetVariablesForDocument } from 'src/hooks/variable';
import { useDocContentChangedCount } from 'src/layout-engine/components/Slate';
import { useMaybeEditorForDocument } from 'src/layout-engine/hooks/editor-hooks';

type DocumentVariable = ReturnType<typeof useGetVariablesForDocument>['variables'][number];

/** How many block levels to climb before giving up on a malformed nesting. */
const MAX_NESTING_DEPTH = 20;

interface LaunchFormInheritance {
  /** Label of the enclosing field already on the launch form, when there is one. */
  inheritedFromLabel: string | null;
  /**
   * True until the document's variables have loaded. Inheritance cannot be
   * decided before then, and the toggle must not be clickable meanwhile: a
   * click in that window writes `addToLaunchForm` onto a child field, which is
   * the drift deriving this was meant to prevent.
   */
  isDeciding: boolean;
  /**
   * True when an enclosing dynamic block choice is already on the launch form,
   * so this field comes along automatically and cannot be removed on its own.
   */
  isInherited: boolean;
}

/**
 * Whether a field is pulled onto the launch form by an enclosing dynamic block.
 *
 * A block's choices are only meaningful together with the questions inside
 * them, so putting the block's driving question on the launch form has to bring
 * its contents too. Otherwise the launcher picks a variant and is never asked
 * what that variant needs. Blocks nest, so this climbs until it finds an
 * enclosing driver that is already included.
 *
 * Derived rather than written down the subtree: nothing to backfill for
 * existing templates, nothing to unwind when the parent is switched off, and a
 * child's stored flag can never drift from what the form actually asks.
 */
export function useLaunchFormInheritance(nodeId: UUID): LaunchFormInheritance {
  const { documentId } = useDocumentParams();
  const editor = useMaybeEditorForDocument(documentId ?? '');
  /**
   * The editor object stays the same across edits, so its content is what
   * makes a field moved into or out of a block re-derive. Reading it alone does
   * not re-render this, so the debounced content-change count subscribes; the
   * debounce also keeps the whole-document walk below off the typing path.
   */
  useDocContentChangedCount();
  const content = editor?.children;
  const { loading, variables } = useGetVariablesForDocument(documentId ?? '', {
    skip: !documentId,
  });

  return useMemo(() => {
    const none = { inheritedFromLabel: null, isDeciding: loading, isInherited: false };
    if (!editor || !content) return none;

    const gates = Queries.getDynamicBlockFieldGates(editor);
    const variableByNodeId = new Map(variables.map(variable => [variable.nodeId, variable]));

    let currentNodeId = nodeId;
    for (let depth = 0; depth < MAX_NESTING_DEPTH; depth++) {
      const gate = gates[currentNodeId];
      if (!gate) return none;

      const driver = variableByNodeId.get(gate.driverFieldId);
      if (driver && getIsOptedOntoLaunchForm(driver))
        return {
          inheritedFromLabel: driver.label || driver.question || null,
          isDeciding: loading,
          isInherited: true,
        };

      // The driver is not itself included; it may still sit inside a block that is.
      currentNodeId = gate.driverFieldId;
    }
    return none;
  }, [content, editor, loading, nodeId, variables]);
}

/**
 * Whether a driver is on the launch form in its own right. CRM-linked fields
 * are filled from the CRM, never asked, so the server leaves them off however
 * they are flagged, and so must this.
 */
function getIsOptedOntoLaunchForm(
  variable: Pick<DocumentVariable, 'addToLaunchForm' | 'integrationLink'>
): boolean {
  return (
    variable.addToLaunchForm &&
    !variable.integrationLink?.integration &&
    !variable.integrationLink?.attributePath
  );
}
